import { useState } from "react";
import {
  Download,
  Loader2,
  MoreHorizontal,
  SlidersHorizontal,
  Tag,
  Trash2,
  UserPlus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AssigneeMenuContent } from "@/components/invoices/AssigneeMenuContent";
import { pluralizeInvoices } from "@/lib/taskQueue";

const ghostClass = "text-zinc-200 hover:bg-white/10 hover:text-white disabled:opacity-50";

/**
 * Bulk actions for the current selection: download, assign, change status,
 * change type, delete, clear.
 *
 * No endpoint here takes more than one guid, so each of these is N requests run
 * through `runQueue` at bounded concurrency and reported as a tally. The bar
 * only shows what it can actually do — the previous version carried Copy, Mark
 * as and Remind as permanently disabled buttons for endpoints that do not
 * exist, which is three controls of pure noise on a toolbar that appears when
 * you are trying to act.
 */
export function InvoiceSelectionBar({
  count,
  onClear,
  assignees = [],
  onRequestAssignees,
  canChangeStatus = false,
  canRecategorise = false,
  canDelete = false,
  onBulkAssign,
  onBulkDelete,
  onBulkDownload,
  onBulkStatus,
  onBulkCategory,
  isWorking = false,
  progress = null,
}) {
  const open = count > 0;
  const [assignOpen, setAssignOpen] = useState(false);

  return (
    <div
      className="selection-bar surface-frost-ink"
      data-open={open ? "true" : "false"}
      role="toolbar"
      aria-label="Selected invoices"
      aria-hidden={!open}
      inert={!open}
    >
      <span className="pe-2 text-sm font-medium text-white" aria-live="polite">
        {/* While a queue is running this reports how far through it is, because
            a bulk action over 50 rows is not instant and a frozen "50 selected"
            reads as a hang. */}
        {isWorking && progress ? (
          <>
            <span className="num">
              {progress.done}/{progress.total}
            </span>{" "}
            {progress.label}
          </>
        ) : (
          <>
            <span className="num">{count}</span> selected
          </>
        )}
      </span>

      {/* Everything between here and the compact menu is the wide composition.
          Measured at 320px the full row wanted 386px in a 273px track, so it
          scrolled — and Delete and Clear, the two most consequential controls,
          sat off-screen behind a swipe on a dark bar with no scroll affordance.
          The dock now recomposes rather than scrolls; both sets are in the DOM
          and the container query picks one, so no second observer is needed and
          the menu's popup stays lazy. */}
      {onBulkDownload && (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          disabled={isWorking}
          onClick={onBulkDownload}
          title="Download selected"
          aria-label={`Download ${pluralizeInvoices(count)}`}
          className={`selection-bar-wide ${ghostClass}`}
        >
          <Download className="size-4" />
        </Button>
      )}

      <DropdownMenu
        open={assignOpen}
        onOpenChange={(next) => {
          if (next) onRequestAssignees?.();
          setAssignOpen(next);
        }}
      >
        <DropdownMenuTrigger
          render={
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              disabled={isWorking}
              title="Assign selected"
              onPointerEnter={onRequestAssignees}
              aria-label="Assign selected invoices"
              className={ghostClass}
            />
          }
        >
          {isWorking ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <UserPlus className="size-4" />
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" side="top" className="w-64 p-1">
          <AssigneeMenuContent
            assignees={assignees}
            label={`Assign ${pluralizeInvoices(count)} to`}
            onSelect={(guid) => {
              setAssignOpen(false);
              onBulkAssign?.(guid);
            }}
            onUnassign={() => {
              setAssignOpen(false);
              onBulkAssign?.(null);
            }}
          />
        </DropdownMenuContent>
      </DropdownMenu>

      {canChangeStatus && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={isWorking}
          onClick={onBulkStatus}
          title="Change status of selected"
          className={`selection-bar-wide rounded-[0.8rem] ${ghostClass}`}
        >
          <SlidersHorizontal data-icon="inline-start" />
          Change status
        </Button>
      )}

      {canRecategorise && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={isWorking}
          onClick={onBulkCategory}
          title="Change type of selected"
          className={`selection-bar-wide rounded-[0.8rem] ${ghostClass}`}
        >
          <Tag data-icon="inline-start" />
          Change type
        </Button>
      )}

      {canDelete && (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          disabled={isWorking}
          onClick={onBulkDelete}
          title="Delete selected"
          aria-label="Delete selected invoices"
          className={`selection-bar-wide ${ghostClass}`}
        >
          <Trash2 className="size-4" />
        </Button>
      )}

      {/* The compact composition: one menu carrying the same actions as words.
          Assign keeps its own control either way — it is a list of people, which
          is a submenu everywhere else and a needless second tap here. */}
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              disabled={isWorking}
              aria-label="More actions for the selected invoices"
              className={`selection-bar-compact ${ghostClass}`}
            />
          }
        >
          <MoreHorizontal className="size-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" side="top" className="w-56 p-1">
          {onBulkDownload && (
            <DropdownMenuItem onClick={onBulkDownload}>
              <Download />
              Download {pluralizeInvoices(count)}
            </DropdownMenuItem>
          )}
          {canChangeStatus && (
            <DropdownMenuItem onClick={onBulkStatus}>
              <SlidersHorizontal />
              Change status
            </DropdownMenuItem>
          )}
          {canRecategorise && (
            <DropdownMenuItem onClick={onBulkCategory}>
              <Tag />
              Change type
            </DropdownMenuItem>
          )}
          {canDelete && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onClick={onBulkDelete}>
                <Trash2 />
                Delete {pluralizeInvoices(count)}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={isWorking}
        onClick={onClear}
        className={`rounded-[0.8rem] ${ghostClass}`}
      >
        Clear
      </Button>
    </div>
  );
}
